import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, UserButton } from '@clerk/clerk-react';
import { Loader2, CreditCard, Calendar, DollarSign, FileText, Ticket, AlertCircle, ExternalLink, CheckCircle2, Download, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall, formatCents, getApiBase } from '@/lib/api';

interface MeResp {
  member: {
    id: string; name: string; email: string | null; phone: string | null;
    status: string; monthly_dues_cents: number; joined_at: string;
    date_of_birth: string | null;
    usaw_id: string | null; usaw_expires_on: string | null;
  };
  active_subscription: {
    plan_name: string | null; plan_amount_cents: number | null; interval: string | null;
    current_period_end_at: string | null;
  } | null;
  recent_payments: Array<{ id: string; amount_cents: number; paid_at: string; method: string; days_added: number | null }>;
  recent_checkins: Array<{ id: string; checked_in_at: string; source: string; class_name: string | null }>;
  class_passes: Array<{ id: string; status: string; source_created_at: string | null; used_at: string | null }>;
  waiver_signed_at: string | null;
}

export default function MyAccount() {
  const { getToken } = useAuth();
  const [data, setData] = useState<MeResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const r = await apiCall<MeResp>('/v1/me', { bearerToken: token });
      setData(r);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function openBillingPortal() {
    setBusy('portal');
    try {
      const token = await getToken();
      const r = await apiCall<{ url: string }>('/v1/me/billing-portal', { method: 'POST', bearerToken: token });
      window.location.href = r.url;
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not open billing portal'); }
    finally { setBusy(null); }
  }

  async function cancelSubscription() {
    if (!confirm('Cancel your subscription? You can re-subscribe any time at /signup.')) return;
    setBusy('cancel');
    try {
      const token = await getToken();
      await apiCall('/v1/me/cancel-subscription', { method: 'POST', bearerToken: token });
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Cancel failed'); }
    finally { setBusy(null); }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  if (err && !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-destructive/40 bg-destructive/5">
          <CardContent className="py-6 space-y-3 text-sm">
            <div className="flex items-start gap-2"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
              <div>
                {err === 'no_membership_for_this_email (404)' ? (
                  <>
                    <p className="font-medium">No membership found for your account.</p>
                    <p className="text-muted-foreground mt-1">
                      Sign up for a plan at <Link to="/signup" className="underline">/signup</Link>, or contact UWC if this is unexpected.
                    </p>
                  </>
                ) : err}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!data) return null;

  const m = data.member;
  const sub = data.active_subscription;
  const passesAvailable = data.class_passes.filter((p) => p.status === 'available').length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="font-semibold">My UWC account</div>
          <UserButton />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{m.name}</CardTitle>
            <CardDescription>
              Member since {new Date(m.joined_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              {' · '}<Badge variant={m.status === 'active' ? 'default' : 'outline'} className="ml-1 capitalize">{m.status}</Badge>
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm grid sm:grid-cols-2 gap-2">
            <div><span className="text-muted-foreground">Email:</span> {m.email ?? '—'}</div>
            <div><span className="text-muted-foreground">Phone:</span> {m.phone ?? '—'}</div>
            {m.usaw_id && (
              <>
                <div><span className="text-muted-foreground">USAW ID:</span> {m.usaw_id}</div>
                <div><span className="text-muted-foreground">USAW expires:</span> {m.usaw_expires_on ?? '—'}</div>
              </>
            )}
          </CardContent>
        </Card>

        {!data.waiver_signed_at && (
          <Card className="border-amber-200 dark:border-amber-900/50">
            <CardContent className="py-3 text-sm flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-amber-700" />
                <span>You haven't signed the waiver yet.</span>
              </div>
              <Button size="sm" asChild><Link to={`/waiver?member_id=${m.id}`}>Sign now <ExternalLink className="h-3.5 w-3.5 ml-1" /></Link></Button>
            </CardContent>
          </Card>
        )}

        {/* Subscription */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><CreditCard className="h-4 w-4" /> Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {sub ? (
              <>
                <div className="text-2xl font-bold tabular-nums">
                  {sub.plan_name}
                  {sub.plan_amount_cents != null && (
                    <span className="text-base font-normal text-muted-foreground"> · {formatCents(sub.plan_amount_cents)}/{sub.interval ?? 'mo'}</span>
                  )}
                </div>
                {sub.current_period_end_at && (
                  <div className="text-xs text-muted-foreground">Next bill date: {new Date(sub.current_period_end_at).toLocaleDateString()}</div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => void openBillingPortal()} disabled={busy === 'portal'}>
                    {busy === 'portal' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ExternalLink className="h-3.5 w-3.5 mr-1" />}
                    Manage billing / update card
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => void cancelSubscription()} disabled={busy === 'cancel'}>
                    Cancel subscription
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                You don't have an active subscription. <Link to="/signup" className="underline">Choose a plan</Link>.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Class passes */}
        {data.class_passes.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Ticket className="h-4 w-4" /> Class passes</CardTitle>
              <CardDescription>{passesAvailable} available · {data.class_passes.length - passesAvailable} used</CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Recent payments */}
        {data.recent_payments.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4" /> Recent payments</CardTitle></CardHeader>
            <CardContent className="p-0"><ul className="divide-y">
              {data.recent_payments.map((p) => (
                <li key={p.id} className="px-4 py-2 text-sm flex items-center justify-between">
                  <div>
                    <div className="font-medium tabular-nums">{formatCents(p.amount_cents)}</div>
                    <div className="text-xs text-muted-foreground">{new Date(p.paid_at.replace(' ', 'T')).toLocaleDateString()} · {p.method}</div>
                  </div>
                  {p.days_added && <Badge variant="outline">+{p.days_added}d</Badge>}
                </li>
              ))}
            </ul></CardContent>
          </Card>
        )}

        {/* Recent check-ins */}
        {data.recent_checkins.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" /> Recent check-ins</CardTitle></CardHeader>
            <CardContent className="p-0"><ul className="divide-y">
              {data.recent_checkins.map((c) => (
                <li key={c.id} className="px-4 py-2 text-sm flex items-center justify-between">
                  <span>{c.class_name ?? <span className="text-muted-foreground">general</span>}</span>
                  <span className="text-xs text-muted-foreground">{new Date(c.checked_in_at.replace(' ', 'T')).toLocaleString()}</span>
                </li>
              ))}
            </ul></CardContent>
          </Card>
        )}

        {data.waiver_signed_at && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 justify-center py-4">
            <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Waiver signed {new Date(data.waiver_signed_at).toLocaleDateString()}
          </p>
        )}

        {/* Privacy: data export + delete request */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Privacy</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-medium">Download my data</div>
                <p className="text-xs text-muted-foreground">A zip of everything we hold about you (profile, payments, check-ins, etc.).</p>
              </div>
              <Button size="sm" variant="outline" onClick={async () => {
                const token = await getToken();
                const r = await fetch(`${getApiBase()}/v1/me/export.zip`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                if (!r.ok) { alert('Export failed'); return; }
                const blob = await r.blob();
                const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `my-data-${new Date().toISOString().slice(0, 10)}.zip`;
                document.body.appendChild(a); a.click(); a.remove();
              }}>
                <Download className="h-3.5 w-3.5 mr-1" /> Export zip
              </Button>
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t">
              <div>
                <div className="text-sm font-medium">Request deletion</div>
                <p className="text-xs text-muted-foreground">Staff review within 7 days. Some records (tax-relevant payments) may be retained per policy.</p>
              </div>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={async () => {
                const reason = prompt('Optional: tell us why (helps us improve).') ?? '';
                if (!confirm('File a deletion request? Staff will review and either action it or follow up.')) return;
                try {
                  const token = await getToken();
                  await apiCall('/v1/me/delete-request', { method: 'POST', bearerToken: token, body: { reason: reason.trim() || null } });
                  alert('Request filed. We\'ll be in touch.');
                } catch (e) { alert(e instanceof Error ? e.message : 'Failed'); }
              }}>
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Request deletion
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
